import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Input,
  Stack,
  Textarea,
  useToast,
  VStack,
  HStack,
  Text,
  Divider,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import { doc, getDoc, updateDoc, Firestore } from "firebase/firestore";
import React, { useState, useEffect } from "react";
import { useFirestore } from "reactfire";

interface KnowledgeTopic {
  title: string;
  summary: string;
}

interface KnowledgeBaseSettingsProps {
  communityId: string;
}

export default function KnowledgeBaseSettings({
  communityId,
}: KnowledgeBaseSettingsProps) {
  const [topics, setTopics] = useState<KnowledgeTopic[]>([
    { title: "", summary: "" },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const toast = useToast();
  const firestore = useFirestore();

  // Fetch existing knowledge base on mount
  useEffect(() => {
    const fetchKnowledgeBase = async () => {
      try {
        const docRef = doc(firestore, "communities", communityId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.knowledgeBase && Array.isArray(data.knowledgeBase)) {
            setTopics(data.knowledgeBase);
          }
        }
      } catch (error) {
        console.error("Error fetching knowledge base:", error);
        toast({
          title: "Error",
          description: "Failed to load knowledge base",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsFetching(false);
      }
    };

    fetchKnowledgeBase();
  }, [communityId, firestore, toast]);

  const handleAddTopic = () => {
    setTopics([...topics, { title: "", summary: "" }]);
  };

  const handleRemoveTopic = (index: number) => {
    setTopics(topics.filter((_, i) => i !== index));
  };

  const handleTopicChange = (
    index: number,
    field: "title" | "summary",
    value: string
  ) => {
    const newTopics = [...topics];
    newTopics[index][field] = value;
    setTopics(newTopics);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    const validTopics = topics.filter((t) => t.title.trim() && t.summary.trim());
    if (validTopics.length === 0) {
      toast({
        title: "Missing content",
        description: "Please add at least one topic with title and summary",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);

    try {
      const docRef = doc(firestore, "communities", communityId);
      await updateDoc(docRef, {
        knowledgeBase: validTopics,
        updatedAt: new Date(),
      });

      toast({
        title: "Success",
        description: `Knowledge base updated with ${validTopics.length} topics`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to update knowledge base",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <Box p={6} borderWidth="1px" borderRadius="lg" bg="white">
        <Heading size="md" mb={4}>
          Knowledge Base
        </Heading>
        <Text>Loading...</Text>
      </Box>
    );
  }

  return (
    <Box p={6} borderWidth="1px" borderRadius="lg" bg="white">
      <Heading size="md" mb={2}>
        Knowledge Base
      </Heading>
      <Text fontSize="sm" color="gray.600" mb={4}>
        Define the knowledge topics that the AI agent can reference when helping
        users. Each topic should have a clear title and comprehensive summary.
      </Text>

      <form onSubmit={handleSubmit}>
        <VStack spacing={4} align="stretch">
          {topics.map((topic, index) => (
            <Box
              key={index}
              p={4}
              borderWidth="1px"
              borderRadius="md"
              bg="gray.50"
            >
              <HStack justify="space-between" mb={3}>
                <Text fontWeight="bold" fontSize="sm" color="gray.700">
                  Topic {index + 1}
                </Text>
                {topics.length > 1 && (
                  <IconButton
                    aria-label="Remove topic"
                    icon={<DeleteIcon />}
                    size="sm"
                    colorScheme="red"
                    variant="ghost"
                    onClick={() => handleRemoveTopic(index)}
                  />
                )}
              </HStack>

              <Stack spacing={3}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">Title</FormLabel>
                  <Input
                    value={topic.title}
                    onChange={(e) =>
                      handleTopicChange(index, "title", e.target.value)
                    }
                    placeholder="e.g., Community Guidelines - No Investment Solicitation"
                    bg="white"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm">Summary</FormLabel>
                  <Textarea
                    value={topic.summary}
                    onChange={(e) =>
                      handleTopicChange(index, "summary", e.target.value)
                    }
                    placeholder="Detailed explanation of this topic..."
                    rows={4}
                    bg="white"
                  />
                </FormControl>
              </Stack>
            </Box>
          ))}

          <Button
            leftIcon={<AddIcon />}
            onClick={handleAddTopic}
            variant="outline"
            size="sm"
          >
            Add Topic
          </Button>

          <Divider />

          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isLoading}
            loadingText="Saving..."
          >
            Save Knowledge Base
          </Button>
        </VStack>
      </form>
    </Box>
  );
}

